import React, { useState, useEffect } from 'react';
import {
  Container,
  Table,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
  Badge,
  Pagination,
  PaginationItem,
  PaginationLink,
} from 'reactstrap';
import axios from 'axios';

const API_BASE_URL = 'https://api.editezy.com/api/poster';

const TrendingPosterCategory = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Modal states
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [subcategoryModal, setSubcategoryModal] = useState(false);

  // Form states
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryName, setCategoryName] = useState('');
  const [categoryImage, setCategoryImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Subcategory states
  const [childCategories, setChildCategories] = useState([]);
  const [childCategoryName, setChildCategoryName] = useState('');
  const [childCategoryImage, setChildCategoryImage] = useState(null);
  const [childImagePreview, setChildImagePreview] = useState('');
  const [selectedChildCategory, setSelectedChildCategory] = useState(null);
  const [editChildModal, setEditChildModal] = useState(false);
  const [deleteChildModal, setDeleteChildModal] = useState(false);
  const [childLoading, setChildLoading] = useState(false);

  // Subcategory Pagination states
  const [childCurrentPage, setChildCurrentPage] = useState(1);
  const [childItemsPerPage] = useState(5);

  // Fetch all categories
  const fetchCategories = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/categories`);
      if (response.data.success) {
        setCategories(response.data.categories);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch child categories
  const fetchChildCategories = async (categoryId) => {
    setChildLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/childcategory/${categoryId}`);
      if (response.data.success) {
        setChildCategories(response.data.childCategories);
        setChildCurrentPage(1); // Reset to first page when new data loads
      }
    } catch (err) {
      console.error('Error fetching child categories:', err);
      setError('Failed to load subcategories. Please try again.');
    } finally {
      setChildLoading(false);
    }
  };

  // Handle child image selection
  const handleChildImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setChildCategoryImage(file);
      setChildImagePreview(URL.createObjectURL(file));
    }
  };

  // Create child category
  const handleCreateChildCategory = async (e) => {
    e.preventDefault();

    if (!childCategoryName.trim()) {
      setError('Subcategory name is required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    const formData = new FormData();
    formData.append('categoryId', selectedCategory._id);
    formData.append('name', childCategoryName.trim());
    if (childCategoryImage) {
      formData.append('image', childCategoryImage);
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/childcategory`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setSuccess('Subcategory created successfully!');
        setChildCategoryName('');
        setChildCategoryImage(null);
        setChildImagePreview('');
        fetchChildCategories(selectedCategory._id);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error creating subcategory:', err);
      setError(err.response?.data?.message || 'Failed to create subcategory');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update child category
  const handleUpdateChildCategory = async (e) => {
    e.preventDefault();

    if (!childCategoryName.trim()) {
      setError('Subcategory name is required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    const formData = new FormData();
    formData.append('categoryId', selectedCategory._id);
    formData.append('name', childCategoryName.trim());
    if (childCategoryImage) {
      formData.append('image', childCategoryImage);
    }

    try {
      const response = await axios.put(
        `${API_BASE_URL}/childcategory/${selectedChildCategory._id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        setSuccess('Subcategory updated successfully!');
        setEditChildModal(false);
        setChildCategoryName('');
        setChildCategoryImage(null);
        setChildImagePreview('');
        fetchChildCategories(selectedCategory._id);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error updating subcategory:', err);
      setError(err.response?.data?.message || 'Failed to update subcategory');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete child category
  const handleDeleteChildCategory = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const response = await axios.delete(`${API_BASE_URL}/childcategory/${selectedChildCategory._id}`);

      if (response.data.success) {
        setSuccess('Subcategory deleted successfully!');
        setDeleteChildModal(false);
        fetchChildCategories(selectedCategory._id);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error deleting subcategory:', err);
      setError(err.response?.data?.message || 'Failed to delete subcategory');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open subcategory modal
  const openSubcategoryModal = (category) => {
    setSelectedCategory(category);
    setChildCategories([]);
    setChildCategoryName('');
    setChildCategoryImage(null);
    setChildImagePreview('');
    setChildCurrentPage(1);
    setSubcategoryModal(true);
    fetchChildCategories(category._id);
  };

  // Open edit child modal
  const openEditChildModal = (childCategory) => {
    setSelectedChildCategory(childCategory);
    setChildCategoryName(childCategory.name);
    setChildImagePreview(childCategory.image || '');
    setChildCategoryImage(null);
    setEditChildModal(true);
  };

  // Open delete child modal
  const openDeleteChildModal = (childCategory) => {
    setSelectedChildCategory(childCategory);
    setDeleteChildModal(true);
  };

  // Pagination logic for main categories
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCategories = categories.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(categories.length / itemsPerPage);

  // Pagination logic for child categories
  const childIndexOfLastItem = childCurrentPage * childItemsPerPage;
  const childIndexOfFirstItem = childIndexOfLastItem - childItemsPerPage;
  const currentChildCategories = childCategories.slice(childIndexOfFirstItem, childIndexOfLastItem);
  const childTotalPages = Math.ceil(childCategories.length / childItemsPerPage);

  // Change page for main categories
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Change page for child categories
  const childPaginate = (pageNumber) => setChildCurrentPage(pageNumber);

  // Go to previous page for main categories
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Go to next page for main categories
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Go to previous page for child categories
  const childGoToPreviousPage = () => {
    if (childCurrentPage > 1) {
      setChildCurrentPage(childCurrentPage - 1);
    }
  };

  // Go to next page for child categories
  const childGoToNextPage = () => {
    if (childCurrentPage < childTotalPages) {
      setChildCurrentPage(childCurrentPage + 1);
    }
  };

  // Generate pagination items with ellipsis for main categories
  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    const halfVisible = Math.floor(maxVisiblePages / 2);

    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    items.push(
      <PaginationItem key="prev" disabled={currentPage === 1}>
        <PaginationLink previous onClick={goToPreviousPage} />
      </PaginationItem>
    );

    // First page
    if (startPage > 1) {
      items.push(
        <PaginationItem key={1}>
          <PaginationLink onClick={() => paginate(1)}>1</PaginationLink>
        </PaginationItem>
      );
      if (startPage > 2) {
        items.push(
          <PaginationItem key="ellipsis-start" disabled>
            <PaginationLink>...</PaginationLink>
          </PaginationItem>
        );
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i} active={currentPage === i}>
          <PaginationLink onClick={() => paginate(i)}>
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(
          <PaginationItem key="ellipsis-end" disabled>
            <PaginationLink>...</PaginationLink>
          </PaginationItem>
        );
      }
      items.push(
        <PaginationItem key={totalPages}>
          <PaginationLink onClick={() => paginate(totalPages)}>
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }

    // Next button
    items.push(
      <PaginationItem key="next" disabled={currentPage === totalPages}>
        <PaginationLink next onClick={goToNextPage} />
      </PaginationItem>
    );

    return items;
  };

  // Generate pagination items with ellipsis for child categories
  const renderChildPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    const halfVisible = Math.floor(maxVisiblePages / 2);

    let startPage = Math.max(1, childCurrentPage - halfVisible);
    let endPage = Math.min(childTotalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    items.push(
      <PaginationItem key="prev" disabled={childCurrentPage === 1}>
        <PaginationLink previous onClick={childGoToPreviousPage} />
      </PaginationItem>
    );

    // First page
    if (startPage > 1) {
      items.push(
        <PaginationItem key={1}>
          <PaginationLink onClick={() => childPaginate(1)}>1</PaginationLink>
        </PaginationItem>
      );
      if (startPage > 2) {
        items.push(
          <PaginationItem key="ellipsis-start" disabled>
            <PaginationLink>...</PaginationLink>
          </PaginationItem>
        );
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i} active={childCurrentPage === i}>
          <PaginationLink onClick={() => childPaginate(i)}>
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    // Last page
    if (endPage < childTotalPages) {
      if (endPage < childTotalPages - 1) {
        items.push(
          <PaginationItem key="ellipsis-end" disabled>
            <PaginationLink>...</PaginationLink>
          </PaginationItem>
        );
      }
      items.push(
        <PaginationItem key={childTotalPages}>
          <PaginationLink onClick={() => childPaginate(childTotalPages)}>
            {childTotalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }

    // Next button
    items.push(
      <PaginationItem key="next" disabled={childCurrentPage === childTotalPages}>
        <PaginationLink next onClick={childGoToNextPage} />
      </PaginationItem>
    );

    return items;
  };

  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCategoryImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Create category
  const handleCreateCategory = async (e) => {
    e.preventDefault();

    if (!categoryName.trim()) {
      setError('Category name is required');
      return;
    }

    if (!categoryImage) {
      setError('Category image is required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    const formData = new FormData();
    formData.append('name', categoryName.trim());
    formData.append('image', categoryImage);

    try {
      const response = await axios.post(`${API_BASE_URL}/categori`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setSuccess('Category created successfully!');
        setCreateModal(false);
        resetForm();
        fetchCategories();

        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error creating category:', err);
      setError(err.response?.data?.message || 'Failed to create category');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update category
  const handleUpdateCategory = async (e) => {
    e.preventDefault();

    if (!categoryName.trim()) {
      setError('Category name is required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    const formData = new FormData();
    formData.append('name', categoryName.trim());
    if (categoryImage) {
      formData.append('image', categoryImage);
    }

    try {
      const response = await axios.put(
        `${API_BASE_URL}/categori/${selectedCategory._id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        setSuccess('Category updated successfully!');
        setEditModal(false);
        resetForm();
        fetchCategories();

        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error updating category:', err);
      setError(err.response?.data?.message || 'Failed to update category');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete category
  const handleDeleteCategory = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const response = await axios.delete(`${API_BASE_URL}/categori/${selectedCategory._id}`);

      if (response.data.success) {
        setSuccess('Category deleted successfully!');
        setDeleteModal(false);
        fetchCategories();

        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(err.response?.data?.message || 'Failed to delete category');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open edit modal and populate form
  const openEditModal = (category) => {
    setSelectedCategory(category);
    setCategoryName(category.name);
    setImagePreview(category.image);
    setCategoryImage(null);
    setEditModal(true);
  };

  // Open delete modal
  const openDeleteModal = (category) => {
    setSelectedCategory(category);
    setDeleteModal(true);
  };

  // View single category
  const handleViewCategory = (category) => {
    setSelectedCategory(category);
    setViewModal(true);
  };

  // Reset form
  const resetForm = () => {
    setCategoryName('');
    setCategoryImage(null);
    setImagePreview('');
    setSelectedCategory(null);
    setError('');
  };

  // Loading state
  if (loading) {
    return (
      <Container className="text-center my-5">
        <Spinner color="primary" />
        <p className="mt-3">Loading categories...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
        <h2 className="text-primary mb-2 mb-md-0">Trending Poster Categories</h2>
        <Button
          color="primary"
          onClick={() => {
            resetForm();
            setCreateModal(true);
          }}
        >
          + Create New Category
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert color="danger" toggle={() => setError('')} className="mb-3">
          {error}
        </Alert>
      )}

      {success && (
        <Alert color="success" toggle={() => setSuccess('')} className="mb-3">
          {success}
        </Alert>
      )}

      {/* Categories Table */}
      <Card className="shadow-sm">
        <CardBody>
          <div className="table-responsive">
            <Table hover bordered striped>
              <thead className="bg-light">
                <tr>
                  <th style={{ width: '5%' }}>#</th>
                  <th style={{ width: '15%' }}>Image</th>
                  <th style={{ width: '20%' }}>Category Name</th>
                  <th style={{ width: '25%' }}>ID</th>
                  <th style={{ width: '15%' }}>Subcategories</th>
                  <th style={{ width: '20%' }}>Created At</th>
                  <th style={{ width: '15%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentCategories.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center">
                      No categories found. Create your first category!
                    </td>
                  </tr>
                ) : (
                  currentCategories.map((category, index) => (
                    <tr key={category._id}>
                      <td className="align-middle">
                        {indexOfFirstItem + index + 1}
                      </td>
                      <td className="align-middle">
                        <img
                          src={category.image}
                          alt={category.name}
                          style={{
                            width: '50px',
                            height: '50px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                          }}
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/50x50?text=No+Image';
                          }}
                        />
                      </td>
                      <td className="align-middle">
                        <strong>{category.name}</strong>
                      </td>
                      <td className="align-middle">
                        <code className="text-muted small">
                          {category._id}
                        </code>
                      </td>
                      <td className="align-middle">
                        <Button
                          size="sm"
                          color="success"
                          onClick={() => openSubcategoryModal(category)}
                        >
                          Manage Subcategories
                        </Button>
                      </td>
                      <td className="align-middle">
                        <Badge color="info" pill>
                          {new Date(category.createdAt).toLocaleDateString()}
                        </Badge>
                        <br />
                        <small className="text-muted">
                          {new Date(category.createdAt).toLocaleTimeString()}
                        </small>
                      </td>
                      <td className="align-middle">
                        <div className="d-flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            color="info"
                            outline
                            onClick={() => handleViewCategory(category)}
                          >
                            View
                          </Button>
                          <Button
                            size="sm"
                            color="warning"
                            outline
                            onClick={() => openEditModal(category)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            color="danger"
                            outline
                            onClick={() => openDeleteModal(category)}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination with Ellipsis for main categories */}
          {categories.length > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap">
              <div className="text-muted small mb-2 mb-md-0">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, categories.length)} of {categories.length} entries
              </div>
              <Pagination className="mb-0">
                {renderPaginationItems()}
              </Pagination>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Create Modal */}
      <Modal isOpen={createModal} toggle={() => setCreateModal(false)} size="md">
        <ModalHeader toggle={() => setCreateModal(false)}>
          Create New Category
        </ModalHeader>
        <Form onSubmit={handleCreateCategory}>
          <ModalBody>
            <FormGroup>
              <Label for="categoryName">Category Name *</Label>
              <Input
                id="categoryName"
                type="text"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="Enter category name"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label for="categoryImage">Category Image *</Label>
              <Input
                id="categoryImage"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                required
              />
              {imagePreview && (
                <div className="mt-2">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px' }}
                  />
                </div>
              )}
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setCreateModal(false)}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? <Spinner size="sm" /> : 'Create'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={editModal} toggle={() => setEditModal(false)} size="md">
        <ModalHeader toggle={() => setEditModal(false)}>
          Edit Category
        </ModalHeader>
        <Form onSubmit={handleUpdateCategory}>
          <ModalBody>
            <FormGroup>
              <Label for="editCategoryName">Category Name *</Label>
              <Input
                id="editCategoryName"
                type="text"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="Enter category name"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label for="editCategoryImage">Category Image (Optional)</Label>
              <Input
                id="editCategoryImage"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              {imagePreview && (
                <div className="mt-2">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px' }}
                  />
                  <small className="text-muted d-block mt-1">Current image preview</small>
                </div>
              )}
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setEditModal(false)}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? <Spinner size="sm" /> : 'Update'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* View Modal */}
      <Modal isOpen={viewModal} toggle={() => setViewModal(false)} size="lg">
        <ModalHeader toggle={() => setViewModal(false)}>
          Category Details
        </ModalHeader>
        <ModalBody>
          {selectedCategory && (
            <div>
              <div className="text-center mb-4">
                <img
                  src={selectedCategory.image}
                  alt={selectedCategory.name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '300px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                  }}
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                  }}
                />
              </div>
              <h4 className="mb-3">{selectedCategory.name}</h4>
              <hr />
              <p><strong>ID:</strong> <code>{selectedCategory._id}</code></p>
              <p><strong>Created At:</strong> {new Date(selectedCategory.createdAt).toLocaleString()}</p>
              <p><strong>Updated At:</strong> {new Date(selectedCategory.updatedAt).toLocaleString()}</p>
              <p>
                <strong>Image URL:</strong>{' '}
                <a href={selectedCategory.image} target="_blank" rel="noopener noreferrer">
                  {selectedCategory.image.length > 50 ? selectedCategory.image.substring(0, 50) + '...' : selectedCategory.image}
                </a>
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setViewModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <ModalHeader toggle={() => setDeleteModal(false)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          <p>Are you sure you want to delete the category <strong>"{selectedCategory?.name}"</strong>?</p>
          <p className="text-danger mb-0"><strong>⚠️ This action cannot be undone!</strong></p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteModal(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDeleteCategory} disabled={isSubmitting}>
            {isSubmitting ? <Spinner size="sm" /> : 'Delete'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Subcategory Modal */}
      <Modal isOpen={subcategoryModal} toggle={() => setSubcategoryModal(false)} size="lg">
        <ModalHeader toggle={() => setSubcategoryModal(false)}>
          Subcategories for "{selectedCategory?.name}"
        </ModalHeader>
        <ModalBody>
          {/* Create Subcategory Form */}
          <Form onSubmit={handleCreateChildCategory} className="mb-4">
            <div className="d-flex flex-column gap-2">
              <div className="d-flex gap-2">
                <Input
                  type="text"
                  placeholder="Enter subcategory name"
                  value={childCategoryName}
                  onChange={(e) => setChildCategoryName(e.target.value)}
                  required
                />
                <Button type="submit" color="primary" disabled={isSubmitting}>
                  {isSubmitting ? <Spinner size="sm" /> : 'Add'}
                </Button>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleChildImageChange}
                  style={{ flex: 1 }}
                />
                {childImagePreview && (
                  <div>
                    <img
                      src={childImagePreview}
                      alt="Preview"
                      style={{
                        width: '50px',
                        height: '50px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </Form>

          {/* Subcategories Table */}
          {childLoading ? (
            <div className="text-center py-3">
              <Spinner color="primary" />
              <p className="mt-2">Loading subcategories...</p>
            </div>
          ) : (
            <>
              <Table hover bordered striped size="sm">
                <thead className="bg-light">
                  <tr>
                    <th style={{ width: '5%' }}>#</th>
                    <th style={{ width: '30%' }}>Name</th>
                    <th style={{ width: '25%' }}>Image</th>
                    <th style={{ width: '25%' }}>ID</th>
                    <th style={{ width: '15%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentChildCategories.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center text-muted">
                        No subcategories found. Add your first subcategory!
                      </td>
                    </tr>
                  ) : (
                    currentChildCategories.map((child, index) => (
                      <tr key={child._id}>
                        <td className="align-middle">{childIndexOfFirstItem + index + 1}</td>
                        <td className="align-middle">
                          <strong>{child.name}</strong>
                        </td>
                        <td className="align-middle">
                          {child.image ? (
                            <img
                              src={child.image}
                              alt={child.name}
                              style={{
                                width: '50px',
                                height: '50px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                              }}
                            />
                          ) : (
                            <span className="text-muted small">No Image</span>
                          )}
                        </td>
                        <td className="align-middle">
                          <code className="text-muted small">{child._id}</code>
                        </td>
                        <td className="align-middle">
                          <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              color="warning"
                              outline
                              onClick={() => openEditChildModal(child)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              color="danger"
                              outline
                              onClick={() => openDeleteChildModal(child)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>

              {/* Pagination for Subcategories */}
              {childCategories.length > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap">
                  <div className="text-muted small mb-2 mb-md-0">
                    Showing {childIndexOfFirstItem + 1} to {Math.min(childIndexOfLastItem, childCategories.length)} of {childCategories.length} entries
                  </div>
                  <Pagination className="mb-0">
                    {renderChildPaginationItems()}
                  </Pagination>
                </div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setSubcategoryModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Child Category Modal */}
      <Modal isOpen={editChildModal} toggle={() => setEditChildModal(false)}>
        <ModalHeader toggle={() => setEditChildModal(false)}>
          Edit Subcategory
        </ModalHeader>
        <Form onSubmit={handleUpdateChildCategory}>
          <ModalBody>
            <FormGroup>
              <Label for="editChildCategoryName">Subcategory Name *</Label>
              <Input
                id="editChildCategoryName"
                type="text"
                value={childCategoryName}
                onChange={(e) => setChildCategoryName(e.target.value)}
                placeholder="Enter subcategory name"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="editChildCategoryImage">Subcategory Image (Optional)</Label>
              <Input
                id="editChildCategoryImage"
                type="file"
                accept="image/*"
                onChange={handleChildImageChange}
              />
              {childImagePreview && (
                <div className="mt-2">
                  <img
                    src={childImagePreview}
                    alt="Preview"
                    style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px' }}
                  />
                  <small className="text-muted d-block mt-1">Current image preview</small>
                </div>
              )}
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setEditChildModal(false)}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? <Spinner size="sm" /> : 'Update'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Delete Child Category Modal */}
      <Modal isOpen={deleteChildModal} toggle={() => setDeleteChildModal(false)}>
        <ModalHeader toggle={() => setDeleteChildModal(false)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          <p>Are you sure you want to delete the subcategory <strong>"{selectedChildCategory?.name}"</strong>?</p>
          <p className="text-danger mb-0"><strong>⚠️ This action cannot be undone!</strong></p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteChildModal(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDeleteChildCategory} disabled={isSubmitting}>
            {isSubmitting ? <Spinner size="sm" /> : 'Delete'}
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default TrendingPosterCategory;